import React from 'react';
import styled from '@emotion/styled';

import { GestureState, gestureInitialState, createPanResponder } from 'helpers/pan-responder';
import { animated, AnimatedValue, Spring, UnknownProps } from 'react-spring';
import { isIOS } from 'utils/uaMatchers';
import { UserAgentContext } from 'hooks/useImageUrlBuilder';
import { routeNameSelector } from 'store/state/selectors/router';
import { Route } from 'config/routes';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { ContentWidthMode } from 'utils/entities';
import { getRightPositionForMapControlElements } from 'components/map/getMapControlPosition';

interface AnimatedValuesMap {
  y: AnimatedValue<number>;
}

// if user is authorized, we need to place MobileWrapper higher (above user navigation menu)
const MobileWrapper = styled.div<{ isIOSDevice: boolean; withOffset?: boolean; }>`
  position: absolute;
  bottom: ${ ({ isIOSDevice, withOffset, theme }) => withOffset ? (isIOSDevice ? theme.spacing(3) : 0) : 0};
  left: 0;
  right: 0;
  z-index: 3; /* mapbox logo & copyright are at 1, 2 */
`;

export const MobileContainer = styled.div<{ isInCarousel: boolean}>`
  background-color: ${props => props.theme.colors.neutrals.white};
  box-shadow: 0 -2px 8px 0 rgba(0, 0, 0, 0.2);
  overflow: hidden;
  border-radius: ${({ theme }) => `${theme.borderRadius.large} ${theme.borderRadius.large} 0 0`};
  position: relative;

  .universal-card-pager-hover {
    display: flex;
  }

  .universal-card-thumbnail-wrapper > div {
    height: 164px;
    padding-top: 0;
  }

  .universal-card-body-wrapper {
    margin: ${({ theme }) => theme.spacing(2)};
    padding: 0;
  }

  ${({ isInCarousel, theme }) => isInCarousel ? '' : `
    &:before {
      content: "";
      z-index: 1;
      position: absolute;
      background-color: ${theme.colors.neutrals.white};
      left: 50%;
      top: ${theme.spacing(2)};
      margin-left: -${theme.spacing(2)};
      width: 32px;
      height: 6px;
      border-radius: ${theme.borderRadius.tiny};
      box-shadow: ${theme.shadow.level2};
    }`
  }
`;

export const MobileContainerModeB = styled.div<{ isInCarousel: boolean}>`
  ${({ theme, isInCarousel }) => `
    background-color: ${theme.colors.neutrals.white};
    overflow: hidden;
    border-radius: ${theme.borderRadius.small};
    border: 1px solid ${theme.colors.neutrals.grey8};
    position: relative;
    ${isInCarousel ? `` : `
      margin: 0 ${theme.spacing(1.5)};
    `}

    .universal-card-pager-hover {
      display: flex;
    }

    .universal-card-thumbnail-wrapper > div {
      height: 164px;
      padding-top: 0;
    }

    .universal-card-body-wrapper {
      margin: ${theme.spacing(2)};
      padding: 0;
    }
  `}
`;

const THRESHOLD_PX = 30;
const CARD_HEIGHT = 224;

interface PropsMobilePopupContainer {
  onSlideDown: () => void;
  routeName: Route;
  disableSlideDown?: boolean;
  style?: React.CSSProperties;
}

interface MobilePopupContainerState {
  gestureState: GestureState;
  lastDy: number;
}

const INTERP: [ number, number ] = [ 0, CARD_HEIGHT ];

class MobilePopupContainerView extends React.Component<PropsMobilePopupContainer, MobilePopupContainerState> {

  public state: MobilePopupContainerState = { gestureState: gestureInitialState, lastDy: null };
  public static contextType = UserAgentContext;

  private handleTransitionEnd = ({ y }: AnimatedValuesMap & Readonly<UnknownProps>) => {
    if (y >= CARD_HEIGHT - 2) this.props.onSlideDown();
  };

  private panHandlers = createPanResponder({
    onStart: (gestureState) => this.setState(() => ({ gestureState })),
    onMove: (gestureState) => this.setState(() => ({ gestureState })),
    onEnd: (gestureState) => this.setState((state) => {
      return {
        lastDy: state.gestureState.dy,
        gestureState,
      };
    }),
    shouldPreventDefault: (_, e) => (e.cancelable),
  });

  public render() {
    const { dy, down } = this.state.gestureState;
    const { children, routeName } = this.props;

    let toY = 0;
    if (down) toY = dy || 0;
    else if (this.state.lastDy > THRESHOLD_PX) toY = CARD_HEIGHT;

    const userAgent = this.context;
    const isIOSDevice = isIOS(userAgent);

    return (
      <MobileWrapper {...(this.props.disableSlideDown ? {} : this.panHandlers)} isIOSDevice={isIOSDevice} withOffset={routeName === Route.Search || routeName === Route.SearchCommercial} style={this.props.style}>
        <Spring onFrame={this.handleTransitionEnd} native to={{ y: toY }} immediate={down}>
          {({ y }) => (
            <animated.div
              style={{
                transform: y.to({
                  range: INTERP,
                  output: INTERP,
                  extrapolate: 'clamp',
                }).to((iY) => `translate3d(0, ${iY}px, 0)`),
              }}
            >
              {children}
            </animated.div>
          )}
        </Spring>

      </MobileWrapper>
    );
  }
}

const mapStateToProps = (state: State) => ({
  routeName: routeNameSelector(state),
});

export const MobilePopupContainer = connect(mapStateToProps)(MobilePopupContainerView);


const POPUP_SIZE = 214;
const POPUP_TRAIL_SIZE = 200;
const POPUP_TO_TRAIL_DISTANCE = 14;
export const MAP_CONTROLS_DEFAULT_OFFSET = 9;


export const MapContainer = styled.div<{
  isSubheaderPresent: boolean;
  isMapExpanded: boolean;
  contentWidthMode?: ContentWidthMode;
}>`
  overflow: hidden;
  &, & .mapboxgl-map {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
  }
  .mapboxgl-ctrl-bottom-right {
    ${({
      theme,
      isMapExpanded,
      contentWidthMode = ContentWidthMode.Default,
    }) => getRightPositionForMapControlElements(theme, isMapExpanded, 0, contentWidthMode)}
  }
  & .mapboxgl-ctrl.mapboxgl-ctrl-group {
    position: absolute;
    ${({ theme }) => theme.media.showAt({ from: 3 })`
      bottom: ${theme.spacing(4)};
    `}
    ${({ theme }) => theme.media.showAt({ to: 2 })`
      bottom: ${theme.spacing(9)};
    `}
    right: 12px;
  }
`;


interface NonMobilePopupContainerProps {
  groupLength: number;
}


const scaleFactor = POPUP_TRAIL_SIZE / POPUP_SIZE;
const commonScale = `scale3d(${scaleFactor}, ${scaleFactor}, ${scaleFactor})`;
const commonScaleSq = `scale3d(${scaleFactor ** 2}, ${scaleFactor ** 2}, ${scaleFactor ** 2})`;

export const NonMobilePopupContainer = styled.div<NonMobilePopupContainerProps>`
  position: relative;
  width: ${POPUP_SIZE}px;
  & > div {
    overflow: hidden;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    box-shadow: ${props => props.theme.shadow.level5};
  }
  .map-preview-card & > div {
    cursor: pointer;
  }
  ${({ groupLength }) => `
    ${groupLength > 1 ? `
      &:before {
        content: "";
        z-index: -1;
      }
    ` : ''}
    ${groupLength > 2 ? `
      &:after {
        content: "";
        z-index: -2;
      }
    ` : ''}
  `}
  &:before, &:after {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #fff;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    box-shadow: ${props => props.theme.shadow.level5};
  }

  .mapboxgl-popup-anchor-right &,
  .mapboxgl-popup-anchor-bottom-right &,
  .mapboxgl-popup-anchor-top-right & {
    &:before {
      transform: translate3d(${-POPUP_TO_TRAIL_DISTANCE}px, 0, 0) ${commonScale};
      transform-origin: left;
    }
    &:after {
      transform: translate3d(${-POPUP_TO_TRAIL_DISTANCE * 2}px, 0, 0) ${commonScaleSq};
      transform-origin: left;
    }
  }
  .mapboxgl-popup-anchor-left &,
  .mapboxgl-popup-anchor-bottom-left &,
  .mapboxgl-popup-anchor-top-left & {
    &:before {
      transform: translate3d(${POPUP_TO_TRAIL_DISTANCE}px, 0, 0) ${commonScale};
      transform-origin: right;
    }
    &:after {
      transform: translate3d(${POPUP_TO_TRAIL_DISTANCE * 2}px, 0, 0) ${commonScaleSq};
      transform-origin: right;
    }
  }
  .mapboxgl-popup-anchor-bottom & {
    &:before {
      transform: translate3d(0, ${-POPUP_TO_TRAIL_DISTANCE}px, 0) ${commonScale};
      transform-origin: top;
    }
    &:after {
      transform: translate3d(0, ${-POPUP_TO_TRAIL_DISTANCE * 2}px, 0) ${commonScaleSq};
      transform-origin: top;
    }
  }
  .mapboxgl-popup-anchor-top & {
    &:before {
      transform: translate3d(0, ${POPUP_TO_TRAIL_DISTANCE}px, 0) ${commonScale};
      transform-origin: bottom;
    }
    &:after {
      transform: translate3d(0, ${POPUP_TO_TRAIL_DISTANCE * 2}px, 0) ${commonScaleSq};
      transform-origin: bottom;
    }
  }
`;
